import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { endOfDay, format, startOfDay, startOfMonth } from "date-fns";
import * as AccountService from "../../../../../db/serviceAccount";
import * as lc from "../../../../modules/localstorage";
import { createRef } from "../../../../../db";
import Account from "../../../../models/Account";
import { syncOrder, syncOrderProps } from "../../../../../api/server/order";
import { useDispatch } from "react-redux";
import { finishSync } from "../../../../modules/order/SyncSlice";
import ModalContainer from "../../../../../shared/components/modals/ModalContainer";
import { toAbsoluteUrl } from "../../../../../resources/helpers";

type SyncModalProp = {
  isOpen?: boolean;
  onClose?: () => void;
  onResultFinished?: () => void;
  onSync?: (prm: boolean) => void;
  syncResult?: (result: any, syncTime: Array<string>) => void;
};

const marketplaceLabels: Record<string, string> = {
  shopee: "Shopee",
  shopee_chat: "Shopee Chat",
  lazada: "Lazada",
  lazada_chat: "Lazada Chat",
  tiktok: "TikTok Shop",
  tiktok_shop: "TikTok Shop",
  tiktok_chat: "TikTok Chat",
  tokopedia: "Tokopedia",
};

const getMarketplaceKey = (type?: string) =>
  (type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getMarketplaceName = (type?: string) => {
  const marketplaceKey = getMarketplaceKey(type);
  if (marketplaceLabels[marketplaceKey])
    return marketplaceLabels[marketplaceKey];

  return type
    ? type
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Marketplace";
};

const getAccountIdentifier = (account: Account) =>
  account.marketplaceShopId || account.searchKey || account.id;

const SyncModal: FC<SyncModalProp> = ({
  isOpen = false,
  onClose,
  onResultFinished,
  onSync,
  syncResult,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [orderDataAll, setOrderData] = useState<Account[]>([]);
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [failedAccountLogoIds, setFailedAccountLogoIds] = useState<string[]>(
    []
  );

  const today = new Date();
  const formattedStartDate = format(startOfMonth(today), "yyyy-MM-dd");
  const formattedEndDate = format(today, "yyyy-MM-dd");
  const formattedStartTime = format(startOfDay(today), "HH:mm:ss");
  const formattedEndTime = format(endOfDay(today), "HH:mm:ss");
  const formattedStartDateToDisplay = format(startOfMonth(today), "dd/MM/yyyy");
  const formattedEndDateToDisplay = format(today, "dd/MM/yyyy");

  const [startDate, setStartDate] = useState(formattedStartDate);
  const [startTime, setStartTime] = useState(formattedStartTime);
  const [startDateToDisplay, setStartDateToDisplay] = useState(
    formattedStartDateToDisplay
  );
  const [endDateToDisplay, setEndDateToDisplay] = useState(
    formattedEndDateToDisplay
  );
  const [endDate, setEndDate] = useState(formattedEndDate);
  const [endTime, setEndTime] = useState(formattedEndTime);
  const [isStartDateValid, setIsStartDateValid] = useState(true);
  const [isStartTimeValid, setIsStartTimeValid] = useState(true);
  const [isEndDateValid, setIsEndDateValid] = useState(true);
  const [isEndTimeValid, setIsEndTimeValid] = useState(true);
  const [isEndDateLessThanStartDate, setIsEndDateLessThanStartDate] =
    useState(true);
  const [isEndTimeLessThanStartTime, setIsEndTimeLessThanStartTime] =
    useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchAccounts = async () => {
      const client = lc.getItemLC(lc.LCName.Client);
      if (!client?.id) return;

      try {
        const accounts = await AccountService.getByClient(
          createRef("clients", client.id)
        );
        if (!mounted || !accounts) return;

        setOrderData(
          accounts.filter((account) => account.type !== "tokopedia")
        );
        setSelectedData(
          accounts
            .filter((account) => account.type !== "tokopedia")
            .map((account) => account.id)
        );
      } catch (error) {
        console.error("Error fetching sync accounts:", error);
      }
    };

    void fetchAccounts();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const start = new Date(`${startDate} ${startTime}`);
    const end = new Date(`${endDate} ${endTime}`);

    setIsStartDateValid(Boolean(startDate));
    setIsStartTimeValid(Boolean(startTime));
    setIsEndDateValid(Boolean(endDate));
    setIsEndTimeValid(Boolean(endTime));
    setIsEndDateLessThanStartDate(
      !endDate || !startDate || endDate >= startDate
    );
    setIsEndTimeLessThanStartTime(
      Number.isNaN(end.getTime()) ||
        Number.isNaN(start.getTime()) ||
        end >= start
    );
  }, [startDate, startTime, endDate, endTime]);

  const handleOnCheckBox = (id: string) => {
    setSelectedData((selectedAccounts) =>
      selectedAccounts.includes(id)
        ? selectedAccounts.filter((selectedId) => selectedId !== id)
        : [...selectedAccounts, id]
    );
  };

  const syncHandler = async () => {
    const client = lc.getItemLC(lc.LCName.Client);
    if (!client?.id) return;

    const fromTime = `${startDate} ${startTime} GMT+0700`;
    const toTime = `${endDate} ${endTime} GMT+0700`;
    const fromTimeToDisplay = `${startDateToDisplay} ${startTime} GMT+0700`;
    const toTimeToDisplay = `${endDateToDisplay} ${endTime} GMT+0700`;
    const request: syncOrderProps = {
      startTime: fromTime,
      endTime: toTime,
      accounts: selectedData,
    };

    lc.setItemLC("isSyncInProgress", "true");
    onClose?.();
    onSync?.(true);
    lc.setItemLC("syncDate", [fromTimeToDisplay, toTimeToDisplay]);
    lc.setItemLC("syncResult", JSON.stringify(request));

    const result = await syncOrder(request, client.id);
    syncResult?.(result, [fromTimeToDisplay, toTimeToDisplay]);
    onResultFinished?.();
    onSync?.(false);
    lc.removeLC("isSync");
    lc.removeLC("isSyncInProgress");
    dispatch(finishSync(result));
  };

  const isInvalidRange =
    !isStartDateValid ||
    !isStartTimeValid ||
    !isEndDateValid ||
    !isEndTimeValid ||
    !isEndDateLessThanStartDate ||
    !isEndTimeLessThanStartTime;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={() => onClose?.()}
      title={t("OrderList.Sync")}
      subtitle={t("OrderList.Sync.Description")}
      size="md"
      autoHeight
      fullscreenOverlay
      footer={
        <div className="tw-flex tw-flex-col-reverse tw-gap-3 sm:tw-flex-row sm:tw-justify-end">
          <button
            type="button"
            className="tw-rounded-lg tw-border tw-border-slate-300 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition-colors hover:tw-bg-slate-50"
            onClick={onClose}
          >
            {t("OrderList.Sync.Cancel")}
          </button>
          <button
            type="button"
            className="tw-rounded-lg tw-bg-blue-600 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors hover:tw-bg-blue-700 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
            onClick={() => void syncHandler()}
            disabled={isInvalidRange || selectedData.length === 0}
          >
            {t("OrderList.Sync.StartSync")}
          </button>
        </div>
      }
    >
      <form
        className="tw-space-y-6 tw-p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <section aria-labelledby="sync-date-range-title">
          <div className="tw-mb-3">
            <h3
              id="sync-date-range-title"
              className="tw-text-sm tw-font-semibold tw-text-slate-900"
            >
              {t("OrderList.Sync.FirstDate")} — {t("OrderList.Sync.EndDate")}
            </h3>
          </div>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
            <DateTimeFields
              dateId="start-date-input"
              timeId="start-time-input"
              label={t("OrderList.Sync.FirstDate")}
              date={startDate}
              time={startTime}
              timeLabel={t("OrderList.Sync.Time")}
              invalidDate={!isStartDateValid}
              invalidTime={!isStartTimeValid}
              onDateChange={(value) => {
                setStartDate(value);
                setStartDateToDisplay(format(new Date(value), "dd/MM/yyyy"));
              }}
              onTimeChange={setStartTime}
              dateError={t("ExportOrder.StartDate.Alert")}
              timeError={t("ExportOrder.StartTime.Alert")}
            />
            <DateTimeFields
              dateId="end-date-input"
              timeId="end-time-input"
              label={t("OrderList.Sync.EndDate")}
              date={endDate}
              time={endTime}
              timeLabel={t("OrderList.Sync.Time")}
              minDate={startDate}
              invalidDate={!isEndDateValid || !isEndDateLessThanStartDate}
              invalidTime={!isEndTimeValid || !isEndTimeLessThanStartTime}
              onDateChange={(value) => {
                setEndDate(value);
                setEndDateToDisplay(format(new Date(value), "dd/MM/yyyy"));
              }}
              onTimeChange={setEndTime}
              dateError={
                !isEndDateValid
                  ? t("ExportOrder.EndDate.Alert1")
                  : t("ExportOrder.EndDate.Alert2")
              }
              timeError={
                !isEndTimeValid
                  ? t("ExportOrder.EndTime.Alert1")
                  : t("ExportOrder.EndTime.Alert2")
              }
            />
          </div>
        </section>

        <section aria-labelledby="sync-accounts-title">
          <div className="tw-mb-3 tw-flex tw-items-baseline tw-justify-between tw-gap-4">
            <div>
              <h3
                id="sync-accounts-title"
                className="tw-text-sm tw-font-semibold tw-text-slate-900"
              >
                {t("OrderList.Sync.Account")}
              </h3>
              <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                {t("OrderList.Sync.AccountHint")}
              </p>
            </div>
            <span className="tw-text-xs tw-font-medium tw-text-slate-500">
              {selectedData.length}/{orderDataAll.length}
            </span>
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
            {orderDataAll.map((account) => {
              const marketplaceKey = getMarketplaceKey(account.type);
              const marketplaceName = getMarketplaceName(account.type);
              const isSelected = selectedData.includes(account.id);
              const hasFailedLogo = failedAccountLogoIds.includes(account.id);

              return (
                <label
                  key={account.id}
                  className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-p-3 tw-transition-colors ${
                    isSelected
                      ? "tw-border-blue-500 tw-bg-blue-50"
                      : "tw-border-slate-200 tw-bg-white hover:tw-border-slate-300 hover:tw-bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleOnCheckBox(account.id)}
                    className="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-blue-600 focus:tw-ring-blue-500"
                    aria-label={`${marketplaceName}: ${
                      account.name || getAccountIdentifier(account)
                    }`}
                  />
                  <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-100 tw-bg-white">
                    {hasFailedLogo || !marketplaceKey ? (
                      <span
                        className="material-symbols-outlined tw-text-slate-400"
                        aria-hidden="true"
                      >
                        storefront
                      </span>
                    ) : (
                      <img
                        src={toAbsoluteUrl(
                          `/media/logos/marketplace/${marketplaceKey}.png`
                        )}
                        alt={`${marketplaceName} logo`}
                        className="tw-h-8 tw-w-8 tw-object-contain"
                        onError={() =>
                          setFailedAccountLogoIds((failedIds) =>
                            failedIds.includes(account.id)
                              ? failedIds
                              : [...failedIds, account.id]
                          )
                        }
                      />
                    )}
                  </span>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900">
                      {account.name || t("OrderList.Sync.UnnamedAccount")}
                    </span>
                    <span className="tw-mt-0.5 tw-block tw-truncate tw-text-xs tw-text-slate-500">
                      {getAccountIdentifier(account)}
                    </span>
                    <span className="tw-mt-1 tw-inline-flex tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-slate-600">
                      {marketplaceName}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {orderDataAll.length === 0 && (
            <p className="tw-rounded-lg tw-border tw-border-dashed tw-border-slate-300 tw-p-4 tw-text-sm tw-text-slate-500">
              {t("OrderList.Sync.NoAccounts")}
            </p>
          )}
        </section>
      </form>
    </ModalContainer>
  );
};

type DateTimeFieldsProps = {
  dateId: string;
  timeId: string;
  label: string;
  date: string;
  time: string;
  timeLabel: string;
  minDate?: string;
  invalidDate: boolean;
  invalidTime: boolean;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  dateError: string;
  timeError: string;
};

const DateTimeFields: FC<DateTimeFieldsProps> = ({
  dateId,
  timeId,
  label,
  date,
  time,
  timeLabel,
  minDate,
  invalidDate,
  invalidTime,
  onDateChange,
  onTimeChange,
  dateError,
  timeError,
}) => (
  <div className="tw-space-y-2">
    <label
      htmlFor={dateId}
      className="tw-block tw-text-sm tw-font-medium tw-text-slate-700"
    >
      {label}
    </label>
    <input
      data-testid={dateId}
      id={dateId}
      name={dateId}
      type="date"
      value={date}
      min={minDate}
      onChange={(event) => onDateChange(event.target.value)}
      className={`tw-w-full tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
        invalidDate ? "tw-border-red-500" : "tw-border-slate-300"
      }`}
      aria-invalid={invalidDate}
    />
    {invalidDate && <p className="tw-text-xs tw-text-red-600">{dateError}</p>}
    <label
      htmlFor={timeId}
      className="tw-block tw-text-xs tw-font-medium tw-text-slate-600"
    >
      {timeLabel}
    </label>
    <input
      data-testid={timeId}
      id={timeId}
      name={timeId}
      type="time"
      step="1"
      value={time}
      onChange={(event) => onTimeChange(event.target.value)}
      className={`tw-w-full tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
        invalidTime ? "tw-border-red-500" : "tw-border-slate-300"
      }`}
      aria-invalid={invalidTime}
    />
    {invalidTime && <p className="tw-text-xs tw-text-red-600">{timeError}</p>}
  </div>
);

export default SyncModal;
